import { MDXProvider } from '@mdx-js/react';
import { Link } from '@tanstack/react-router';
import { ChevronRightIcon, ExternalLinkIcon } from 'lucide-react';
import { type ComponentType, lazy, Suspense, useMemo } from 'react';
import { Spinner } from '~/modules/common/spinner';
import { type DocsTile, docsConfig, getDocPageLoader, getResolvedDocPageComponent } from '~/modules/page/content';
import { mdxComponents } from '~/modules/page/mdx-components';
import { tw } from '~/utils/tw';

/** The /docs landing page, driven by the global docs config (content root index.mdx frontmatter). */
export function DocsLandingPage() {
  // Body resolved by the index route loader; the lazy path covers other callers and the no-body case
  const Content = useMemo<ComponentType<{ components?: typeof mdxComponents }> | null>(() => {
    const resolved = getResolvedDocPageComponent('');
    if (resolved) return resolved;
    const loader = getDocPageLoader('');
    return loader ? lazy(async () => ({ default: await loader() })) : null;
  }, []);

  return (
    <div className="container">
      <div className="mx-auto max-w-208">
        <div className="prose dark:prose-invert max-w-none">
          <h1 className="pt-6">{docsConfig.title}</h1>
          {Content && (
            <Suspense fallback={<Spinner className="my-16" />}>
              <MDXProvider components={mdxComponents}>
                <Content />
              </MDXProvider>
            </Suspense>
          )}
          <p className="not-prose text-muted-foreground text-sm">v{__PKG_VERSION__}</p>
          {docsConfig.tiles.length > 0 && (
            <div className="not-prose mt-8 grid gap-3 sm:grid-cols-2">
              {docsConfig.tiles.map((tile) => (
                <DocsTileCard key={`${tile.to}-${tile.label}`} tile={tile} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** Landing tile; internal targets go through the router. */
function DocsTileCard({ tile }: { tile: DocsTile }) {
  const isInternal = tile.to.startsWith('/');
  const cardClass = tw('group flex items-center gap-3 rounded-lg border p-4 transition-colors hover:bg-accent/50');
  const TrailingIcon = isInternal ? ChevronRightIcon : ExternalLinkIcon;
  const inner = (
    <>
      <div className="min-w-0 flex-1">
        <h3 className="link-decoration group-active:link-decoration-strong font-medium text-base group-hover:underline">{tile.label}</h3>
        {tile.description && <p className="mt-1 line-clamp-2 text-muted-foreground text-sm">{tile.description}</p>}
      </div>
      <TrailingIcon className="shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
    </>
  );

  if (isInternal) {
    return (
      <Link to={tile.to} draggable={false} className={cardClass}>
        {inner}
      </Link>
    );
  }
  return (
    <a href={tile.to} target="_blank" rel="noreferrer" draggable={false} className={cardClass}>
      {inner}
    </a>
  );
}
